import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { NotFound } from '@/components/molecules/NotFound';
import { cageScopedSearch } from '@/contexts/cage-filter-context';
import LocationCompare from '@/routes/Client/Location/pages/LocationCompare';
import LocationLiceReportContainer from '@/routes/Client/Location/pages/LocationLiceReport/LocationLiceReportContainer';
import UserManagementView from '@/routes/shared/UserManagementPage';

import HelpPageContainer from '../components/Help/HelpPageContainer';
import { MoveBioscopeContainer } from '../MoveBioScope/MoveBioscopeContainer';

import { CagePageWrapper } from './CagePageWrapper';
import { PenFishGroupGuard } from './PenFishGroupGuard';
import { PenGate } from './PenGate';
import BehaviorLayout from './pages/CageBehavior/BehaviorLayout';
import CageBioscopeDetailContainer from './pages/CageBioscopeDetail';
import CageGraph from './pages/CageDashboard/CageAllGraphs';
import CageBioscopeInspectionContainer from './pages/CageDashboard/CageBioscopeStatus/CageBioscopeInspectionContainer';
import CageDashboard from './pages/CageDashboard/CageDashboard';
import CageImagePageContainer from './pages/CageDashboard/CageImages/CageImagePageContainer';
import CageLiceCaligus from './pages/CageLice/CageLiceCaligus';
import CageLiceDashboard from './pages/CageLice/CageLiceDashboard';
import CageLiceDetections from './pages/CageLice/CageLiceDetections';
import CageLiceFemaleLice from './pages/CageLice/CageLiceFemaleLice';
import CageLiceGraphs from './pages/CageLice/CageLiceGraphs';
import CageLiceMovableLice from './pages/CageLice/CageLiceMovable';
import CageWeightDashboardContainer from './pages/CageWeight/CageWeightDashboard/CageWeightDashboardContainer';
import CageWeightDistribution from './pages/CageWeight/CageWeightDistribution/CageWeightDistributionProviderContainer';
import CageWeightGraphsProviderContainer from './pages/CageWeight/CageWeightGraphs';
import CageWeightGrowthProviderContainer from './pages/CageWeight/CageWeightGrowth';
import CageWinchControlContainer from './pages/CageWinchControl/CageWinchControlContainer';
import HealthLayout from './pages/Health/HealthLayout';
import ReportsLayout from './pages/Reports/ReportsLayout';
import CageSettings from './pages/Settings';
import { WelfareRoutes } from './WelfareRoutes';

interface CageRoutesProps {
    clientId: number;
    locationId: number;
}

/** Every page under `/c/:clientId/location/:locationId/pen/:cageId`, inside the cage providers. */
export function CageRoutes(props: CageRoutesProps) {
    const { clientId, locationId } = props;
    const params = useParams();
    const cageId = Number.parseInt(params.cageId ?? '', 10);
    const ids = { clientId, locationId, cageId };
    const { search } = useLocation();

    return (
        <PenFishGroupGuard {...ids} penPage={params['*'] ?? ''}>
            <CagePageWrapper>
                <PenGate locationId={locationId}>
                    <Routes>
                        {/* Dashboard sub-pages */}
                        <Route
                            path="dashboard/images"
                            element={<CageImagePageContainer {...ids} />}
                        />
                        <Route path="dashboard/graphs" element={<CageGraph />} />
                        <Route
                            path="dashboard/bioscope"
                            element={<CageBioscopeInspectionContainer {...ids} />}
                        />

                        {/* Weight sub-pages */}
                        <Route path="weight/distribution" element={<CageWeightDistribution />} />
                        <Route
                            path="weight/growth"
                            element={<CageWeightGrowthProviderContainer />}
                        />
                        <Route
                            path="weight/graphs"
                            element={<CageWeightGraphsProviderContainer {...ids} />}
                        />
                        <Route path="weight" element={<CageWeightDashboardContainer {...ids} />} />

                        {/* Lice sub-pages */}
                        <Route path="lice/detections" element={<CageLiceDetections {...ids} />} />
                        <Route path="lice/female" element={<CageLiceFemaleLice {...ids} />} />
                        <Route
                            path="lice/ovigerousfemale"
                            element={<CageLiceFemaleLice {...ids} />}
                        />
                        <Route path="lice/mobile" element={<CageLiceMovableLice {...ids} />} />
                        <Route path="lice/caligus" element={<CageLiceCaligus {...ids} />} />
                        <Route path="lice/graphs" element={<CageLiceGraphs {...ids} />} />
                        <Route
                            path="lice/lice-report"
                            element={
                                <LocationLiceReportContainer
                                    clientId={clientId}
                                    locationId={locationId}
                                />
                            }
                        />
                        <Route path="lice" element={<CageLiceDashboard {...ids} />} />

                        {/* Welfare sub-pages, only for Bioscope 1.0 apart from fish speed */}
                        <Route path="welfare/*" element={<WelfareRoutes {...ids} />} />

                        {/* Modules with routes of their own */}
                        <Route path="behavior/*" element={<BehaviorLayout />} />
                        <Route path="health/*" element={<HealthLayout />} />
                        <Route path="reports/*" element={<ReportsLayout />} />

                        {/* Winch control */}
                        <Route
                            path="move-bioscope"
                            element={<CageWinchControlContainer {...ids} />}
                        />

                        {/* Compare: the location view, reachable from the cage sidebar too */}
                        <Route
                            path="compare"
                            element={
                                <LocationCompare clientId={clientId} locationId={locationId} />
                            }
                        />

                        <Route path="settings" element={<CageSettings />} />

                        {/* Hidden route (not in the sidebar) */}
                        <Route
                            path="bioscope/:bioscopeId"
                            element={<CageBioscopeDetailContainer />}
                        />

                        {/* Static pages */}
                        <Route
                            path="change-pen"
                            element={
                                <MoveBioscopeContainer
                                    clientId={clientId}
                                    locationId={locationId}
                                />
                            }
                        />
                        <Route path="users" element={<UserManagementView clientId={clientId} />} />
                        <Route path="help" element={<HelpPageContainer />} />

                        <Route path="dashboard" element={<CageDashboard {...ids} />} />
                        {/* Keeps the cage-scoped query, so `fg` survives the hop. */}
                        <Route
                            index
                            element={
                                <Navigate
                                    to={{ pathname: 'dashboard', search: cageScopedSearch(search) }}
                                    replace
                                />
                            }
                        />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </PenGate>
            </CagePageWrapper>
        </PenFishGroupGuard>
    );
}
